import { useRef, useEffect, useCallback } from 'react';

type ThrottleOptions = {
  leading?: boolean;
  trailing?: boolean;
};

export function useThrottle<T extends (...args: any[]) => any>(
  callback: T,
  delay: number = 2000,
  options: ThrottleOptions = { leading: true, trailing: false }
): T {
  const { leading = true, trailing = false } = options;
  const callbackRef = useRef<T>(callback);
  const isThrottledRef = useRef<boolean>(false);
  const lastArgsRef = useRef<any[] | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  useEffect(() => () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  }, []);

  return useCallback(
    ((...args: any[]) => {
      if (isThrottledRef.current) {
        if (trailing) {
          lastArgsRef.current = args;
        }
        return;
      }

      isThrottledRef.current = true;

      if (leading) {
        callbackRef.current(...args);
      } else if (trailing) {
        lastArgsRef.current = args;
      }

      timeoutRef.current = setTimeout(() => {
        const lastArgs = lastArgsRef.current;
        isThrottledRef.current = false;
        lastArgsRef.current = null;

        if (trailing && lastArgs) {
          callbackRef.current(...lastArgs);
        }
      }, delay);
    }) as T,
    [delay, leading, trailing]
  );
}
